import React, { useMemo, useState } from 'react';
import { Star, ShoppingCart, Eye, PackageX, ZoomIn } from 'lucide-react';
import ZoomViewer, { isTouchDevice } from './ZoomViewer';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useStore, getEffectivePrice } from '../context/StoreContext';
import { CURRENCY } from '../mock/mock';
import ProductModal from './ProductModal';
import { Bubbles, SparkleAccents, glowClass } from './Decor';
import { toast } from 'sonner';
import { TiltCard, Reveal, useMagnifier } from './Effects';

const categories = ['All Products', 'Home Care', 'Personal Care'];

const ProductCard = ({ product, onQuickView, onZoom }) => {
  const { settings, addToCart } = useStore();
  const { finalPrice, discountPercent, hasDiscount } = getEffectivePrice(product, settings);
  const outOfStock = product.stock <= 0;
  const lowStock = product.stock > 0 && product.stock <= 5;
  const { imgRef, lensEl, handlers } = useMagnifier(product.image);
  const touch = isTouchDevice();

  return (
    <TiltCard className="h-full rounded-2xl">
    <div data-testid={`product-card-${product.id}`} className="product-card h-full group relative glass-card rounded-2xl border border-white/60 overflow-hidden shadow-[0_8px_30px_-12px_rgba(21,154,59,0.18)] hover:shadow-[0_24px_50px_-16px_rgba(21,154,59,0.35)] hover:-translate-y-1.5 transition-all duration-300 flex flex-col">
      {/* badges */}
      <div className="absolute top-3 left-3 z-20 flex flex-col gap-1.5 items-start">
        {product.bestseller && (
          <Badge className="bg-[#f5b400] text-[#3d2f00] hover:bg-[#f5b400] font-semibold shadow-sm">Bestseller</Badge>
        )}
        {hasDiscount && (
          <Badge className="bg-[#e11d1d] text-white hover:bg-[#e11d1d] font-semibold shadow-sm">-{discountPercent}%</Badge>
        )}
      </div>

      <div {...(touch ? {} : handlers)} onClick={() => (touch ? onZoom(product) : onQuickView(product))} data-testid={`product-zoom-area-${product.id}`} className="relative overflow-hidden h-60 flex items-center justify-center p-4 cursor-zoom-in">
        {/* glow pedestal + clipart */}
        <div className={`absolute inset-0 ${glowClass(product)}`} />
        <Bubbles count={6} />
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <SparkleAccents />
        </div>
        <div className="shine-sweep absolute inset-0 z-10" />
        {/* soft reflection */}
        <div className="product-shadow-ellipse absolute bottom-4 left-1/2 -translate-x-1/2 h-4 w-28" />
        <img
          ref={imgRef}
          src={product.image}
          alt={product.name}
          className="bottle-pop relative z-10 h-full w-auto object-contain drop-shadow-[0_14px_18px_rgba(0,0,0,0.18)]"
        />
        {lensEl}
        <span className="zoom-hint absolute bottom-3 right-3 z-20 bg-white/85 backdrop-blur text-foreground text-[11px] font-semibold px-2.5 py-1 rounded-full shadow flex items-center gap-1">
          <ZoomIn className="h-3.5 w-3.5 text-primary" /> {touch ? 'Tap to zoom' : 'Click to view'}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 mb-1">
          <Star className="h-3.5 w-3.5 fill-[#f5b400] text-[#f5b400]" />
          <span className="text-xs font-medium text-muted-foreground">{product.rating} ({product.reviewsCount})</span>
          <Badge variant="outline" className="ml-auto text-[11px] border-primary/30 text-primary">{product.size}</Badge>
        </div>
        <h3 className="font-display font-bold text-lg text-foreground leading-snug">{product.name}</h3>
        <p className="text-sm text-primary font-medium">{product.tagline}</p>
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2 flex-1">{product.description}</p>

        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="font-display font-extrabold text-xl text-foreground">{CURRENCY}{finalPrice}</span>
              {hasDiscount && (
                <span className="text-sm text-muted-foreground line-through">{CURRENCY}{product.price}</span>
              )}
            </div>
            {lowStock && <span className="text-xs text-[#e11d1d] font-medium">Only {product.stock} left!</span>}
            {outOfStock && <span className="text-xs text-muted-foreground font-medium">Out of stock</span>}
          </div>
        </div>

        <div className="mt-4 flex gap-2">
        <Button
          data-testid={`product-quickview-${product.id}`}
          variant="outline"
          onClick={() => onQuickView(product)}
          className="shrink-0 border-primary/30 text-primary hover:bg-primary/5 px-3"
          aria-label="Quick view"
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Button
          data-testid={`add-to-cart-${product.id}`}
          disabled={outOfStock}
          onClick={() => {
            addToCart(product);
            toast.success(`${product.name} added to cart`);
          }}
          className={`flex-1 bg-primary hover:bg-primary/90 text-white font-semibold disabled:opacity-50 ${outOfStock ? '' : 'btn-shimmer'}`}
        >
          {outOfStock ? <><PackageX className="h-4 w-4 mr-2" /> Sold Out</> : <><ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart</>}
        </Button>
        </div>
      </div>
    </div>
    </TiltCard>
  );
};

const ProductsSection = () => {
  const { products } = useStore();
  const [active, setActive] = useState('All Products');
  const [selected, setSelected] = useState(null);
  const [zoomed, setZoomed] = useState(null);

  const filtered = useMemo(() => {
    if (active === 'All Products') return products;
    return products.filter((p) => p.category === active);
  }, [products, active]);

  return (
    <section id="products" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-primary font-semibold uppercase tracking-wide text-sm mb-2">Our Product Lineup</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground">Products We <span className="gradient-text-animated">Deal In</span></h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Specialized 5L solutions for your home sanitation and personal wellness needs.
          </p>
          <p className="mt-2 text-xs text-primary/80 font-medium">Hover over a bottle to magnify its label · on mobile, tap to pinch-zoom</p>
        </div>

        <div className="flex justify-center gap-2 mb-10 flex-wrap">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                active === c ? 'bg-primary text-white shadow-md' : 'bg-secondary text-foreground/70 hover:bg-primary/10'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 110} className="h-full">
              <ProductCard product={p} onQuickView={setSelected} onZoom={setZoomed} />
            </Reveal>
          ))}
        </div>
      </div>

      <ProductModal product={selected} open={!!selected} onClose={() => setSelected(null)} />
      <ZoomViewer src={zoomed?.image} alt={zoomed?.name} open={!!zoomed} onClose={() => setZoomed(null)} />
    </section>
  );
};

export default ProductsSection;
